<template>
  <div class="v5" :data-theme="theme">
    <div class="v5-app" :class="{ 'v5-rail': rail, 'is-pinned': pinned }">
      <aside id="v5-side" class="v5-side" :class="{ 'is-open': menuOpen }" :aria-label="t('v5.nav.main')">
        <router-link class="v5-logo" to="/v5" :aria-label="t('v5.shell.logo_alt')">
          <img class="v5-logo__full" :src="Logo" alt=""><img class="v5-logo__icon" :src="Mark" alt="">
        </router-link>
        <nav>
          <div v-for="(g, i) in nav" :key="i" class="v5-grp">
            <span v-if="g.group" class="v5-grp__label">{{ t(`v5.group.${g.group}`) }}</span>
            <router-link
              v-for="item in g.items"
              :key="item.key"
              class="v5-nav"
              :to="linkFor(item)"
              :aria-current="item.key === page ? 'page' : undefined"
              @click="menuOpen = false"
              @mouseenter="warm(item)"
              @focus="warm(item)"
            >
              <component :is="item.icon" class="i" aria-hidden="true" />
              <span class="v5-nav__label">{{ t(`v5.nav.${item.key}`) }}<span v-if="statusFor(item)" class="sr">, {{ t(`v5.shell.mm_${statusFor(item)}`) }}</span></span>
              <span v-if="statusFor(item) === 'searching'" class="v5-spin v5-nav__status" aria-hidden="true" />
              <PlayIcon v-else-if="statusFor(item)" class="i v5-good v5-nav__status" aria-hidden="true" />
              <small v-else-if="item.badge" class="v5-nav__badge" :class="`is-${item.badge}`">{{ t(`v5.shell.badge_${item.badge}`) }}</small>
            </router-link>
          </div>
        </nav>
        <div class="v5-side__foot">
          <V5GetStarted v-if="!rail" :user="user" />
          <a class="v5-nav" href="https://wiki.refrag.gg/" target="_blank" rel="noopener">
            <QuestionMarkCircleIcon class="i" aria-hidden="true" /><span class="v5-nav__label">{{ t('v5.shell.help') }}</span>
          </a>
          <a class="v5-nav" href="https://discord.gg/refrag" target="_blank" rel="noopener">
            <ChatBubbleLeftRightIcon class="i" aria-hidden="true" /><span class="v5-nav__label">{{ t('v5.shell.support') }}</span>
          </a>
          <V5TeamSwitcher :team="activeTeam" :teams="user.teams || []" @switch="switchTeam" />
        </div>
      </aside>
      <div class="v5-scrim" :class="{ 'is-open': menuOpen }" aria-hidden="true" @click="menuOpen = false" />

      <div class="v5-main">
        <header class="v5-top">
          <button class="v5-ib v5-top__menu" type="button" :aria-label="t('v5.shell.open_menu')" :aria-expanded="String(menuOpen)" aria-controls="v5-side" @click="menuOpen = !menuOpen">
            <Bars3Icon class="i" aria-hidden="true" />
          </button>
          <router-link class="v5-top__logo" to="/v5" :aria-label="t('v5.shell.logo_alt')"><img :src="Logo" alt=""></router-link>
          <div class="v5-top__right">
            <V5ServerControl
              :user="user"
              :server="activeServer"
              :team-servers="activeServers"
              :can-start-server="canStartServer"
              :launcher-disabled="serverLauncherDisabled"
              :terminate-server="terminateServer"
              @subscribe="subscribeOpen = true"
            />
            <span class="v5-sep" aria-hidden="true" />
            <V5Streak :user="user" />
            <V5Bell :unseen="unseenNotices" @see="seeNotice" @clear="clearNotices" />
            <V5AccountMenu
              :user="user"
              :theme="theme"
              :classic="classicPath"
              @theme="toggle"
              :panel-open="profileShown"
              @panel="toggleProfile"
              @sign-out="signOut"
            />
          </div>
        </header>
        <main class="v5-page">
          <slot />
        </main>
      </div>
    </div>

    <V5Toast />
    <V5Dialog v-if="showNotifications && notifications[0]" id="v5-important" :title="notifications[0].title" @close="seeNotification">
      <img v-if="notifications[0].image" class="v5-dialog__img" :src="notifications[0].image" alt="">
      <p class="v5-dialog__text">{{ notifications[0].description }}</p>
      <button class="v5-btn-p" type="button" @click="openImportant">{{ notifications[0].button_text || t('v5.shell.learn_more') }}</button>
    </V5Dialog>
    <V5Dialog v-if="subscribeOpen" id="v5-subscribe" :title="t('v5.shell.subscribe_title')" @close="subscribeOpen = false">
      <template #icon><LockClosedIcon class="i" aria-hidden="true" /></template>
      <iframe
        class="v5-dialog__video"
        :title="t('v5.shell.subscribe_video')"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
        src="https://www.youtube.com/embed/QwhgL4SsEfY?autoplay=1&mute=1"
      />
      <p class="v5-dialog__text">{{ t('v5.shell.subscribe_pitch') }}</p>
      <ul class="v5-checks">
        <li><CheckIcon class="i" aria-hidden="true" /><span>{{ t('v5.shell.subscribe_price', { price: '$5.83' }) }}</span></li>
        <li><CheckIcon class="i" aria-hidden="true" /><span>{{ t('v5.shell.subscribe_servers') }}</span></li>
        <li><CheckIcon class="i" aria-hidden="true" /><span>{{ t('v5.shell.subscribe_content') }}</span></li>
      </ul>
      <router-link class="v5-btn-p" :to="upgradeLink">{{ t('v5.shell.upgrade_now') }}</router-link>
    </V5Dialog>
    <!-- Match found, party invites and LAN seat offers: the classic block's logic, v5 popups -->
    <V5MatchmakingBlock :lobby-route-name="lobbyRouteName" />
    <!-- Profile sidebar (matchmaking players): overlay from the avatar, or pinned -->
    <template v-if="profileShown">
      <div v-if="!pinned" class="v5-prof-scrim" aria-hidden="true" @click="closeProfile" />
      <V5ProfileSidebar
        :user="user"
        :theme="theme"
        :classic="classicPath"
        :pinned="pinned"
        @close="closeProfile"
        @theme="toggle"
        @sign-out="signOut"
      />
    </template>
  </div>
</template>

<script setup>
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import {
  Bars3Icon, ChatBubbleLeftRightIcon, CheckIcon, LockClosedIcon, PlayIcon, QuestionMarkCircleIcon,
} from '@heroicons/vue/24/outline';
import V5AccountMenu from './V5AccountMenu.vue';
import V5Bell from './V5Bell.vue';
import V5Dialog from './V5Dialog.vue';
import V5GetStarted from './V5GetStarted.vue';
import V5ServerControl from './V5ServerControl.vue';
import V5Streak from './V5Streak.vue';
import V5TeamSwitcher from './V5TeamSwitcher.vue';
import V5Toast from './V5Toast.vue';
import V5MatchmakingBlock from './V5MatchmakingBlock.vue';
import V5ProfileSidebar from './V5ProfileSidebar.vue';
import useV5Shell from '../useV5Shell';
import { useV5Theme } from '../useV5Theme';
import {
  V5_NAV, classicFor, v5OrClassic, v5Path,
} from '../nav';
import { openNotificationLink } from '../../utils/notification_link';
import { prefetchRoutes } from '../../utils/stale_chunks';
import { clearSignedOutUser, confirmSignOut } from '../../utils/session';
import Logo from '../../utils/logo';
import Mark from '../../assets/logos/refrag-icon.svg';
import '../v5.css';

// The v5 shell: sidebar, top bar and every popup, plus the session bootstrap (useV5Shell).
// V5ShellRoute mounts it once for the v5 audience, once onboarding is done; the props are the
// current page's, from its V5Layout.
const props = defineProps({
  page: { type: String, required: true },
  title: { type: String, default: '' },
  classic: { type: String, default: '' },
  rail: { type: Boolean, default: false },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { theme, toggle } = useV5Theme();
const {
  user, activeTeam, switchTeam, notifications, showNotifications, seeNotification,
  matchmakingStatus,
  activeLobby, unseenNotices, seeNotice, clearNotices, activeServer, activeServers, canStartServer,
  serverLauncherDisabled, terminateServer,
} = useV5Shell();

const title = computed(() => props.title || (props.page ? t(`v5.nav.${props.page}`) : ''));
useHead({ title: () => `Refrag | ${title.value.replace(/^Refrag\s+/, '')}` });
const classicPath = computed(() => props.classic || classicFor(props.page));

const nav = computed(() => V5_NAV
  .map((g) => ({ ...g, items: g.items.filter((item) => !item.show || item.show(user.value)) }))
  .filter((g) => g.items.length));
const statusFor = (item) => (item.key === 'matchmaking' ? matchmakingStatus.value : null);
const lobbyRouteName = computed(() => (router.hasRoute('V5MatchmakingLobby') ? 'V5MatchmakingLobby' : undefined));
// In a match, the matchmaking item goes to the match room, as in the classic sidebar. A player
// in through a LAN party only has no regular queue: theirs goes to the LAN hub (their night).
function linkFor(item) {
  if (item.key === 'matchmaking' && activeLobby.value) {
    const id = activeLobby.value.uuid;
    return v5OrClassic(router, `/v5/matchmaking/lobby/${id}`, `/matchmaking/lobby/${id}`);
  }
  if (item.key === 'matchmaking' && user.value.matchmaking_lan_only) return v5OrClassic(router, '/v5/lan', '/lan');
  return v5Path(item.key);
}
// Page chunks: a sidebar link's on hover or focus, every v5 page's once the browser is idle
const warm = (item) => prefetchRoutes(router.resolve(linkFor(item)).matched);
function prefetchAll() {
  prefetchRoutes(router.getRoutes().filter((r) => r.path.startsWith('/v5')));
}
const upgradeLink = computed(() => v5OrClassic(router, '/v5/upgrade', '/upgrade'));

const menuOpen = ref(false);
const subscribeOpen = ref(false);
const profileOpen = ref(false);
watch(() => route.fullPath, () => {
  menuOpen.value = false; subscribeOpen.value = false; profileOpen.value = false;
});

// The profile sidebar is pinned open on every Matchmaking page on wide screens, as the classic
// layout's pinProfileSidebar, except the LAN control room, which needs the width
const wideQuery = window.matchMedia?.('(min-width: 80rem)');
const wide = ref(!!wideQuery?.matches);
const onWide = (e) => { wide.value = e.matches; };
const pinned = computed(() => props.page === 'matchmaking' && route.name !== 'V5LanControlRoom'
  && wide.value && !!user.value?.can_use_matchmaking);
const profileShown = computed(() => pinned.value || profileOpen.value);
function closeProfile() {
  if (!profileOpen.value) return;
  profileOpen.value = false;
  nextTick(() => document.getElementById('v5-me')?.focus());
}
function toggleProfile() {
  if (pinned.value) document.getElementById('v5-profile')?.focus();
  else if (profileOpen.value) closeProfile();
  else profileOpen.value = true;
}

function openImportant() {
  const { link } = notifications.value[0];
  seeNotification();
  openNotificationLink(router, link);
}

const auth = useAuth();
function signOut() {
  if (!confirmSignOut(user.value.username)) return;
  clearSignedOutUser();
  auth.logout({ makeRequest: true, redirect: { name: 'Login' } });
}

const onKey = (e) => {
  if (e.key !== 'Escape' || e.target.closest?.('dialog')) return;
  menuOpen.value = false;
  if (!pinned.value) closeProfile();
};
// v5 type (Inter, JetBrains Mono) loads only when someone opens the redesign
const FONTS = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap';
function loadFonts() {
  if (document.querySelector(`link[href="${FONTS}"]`)) return;
  document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'stylesheet', href: FONTS }));
}
onMounted(() => {
  loadFonts();
  if (window.requestIdleCallback) window.requestIdleCallback(prefetchAll, { timeout: 3000 });
  else setTimeout(prefetchAll, 1000);
  document.addEventListener('keydown', onKey);
  wideQuery?.addEventListener?.('change', onWide);
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey);
  wideQuery?.removeEventListener?.('change', onWide);
});
</script>
